import { LineChartOutlined } from '@ant-design/icons'
import type { Chart } from '@antv/g2'
import { DatePicker, Typography } from 'antd'
import moment from 'moment'
import React from 'react'

import { useGetStatisticsQuery } from '@/generated/graphql'

import s from './monitor.module.less'
import { compensateWeekData, getAllThreeWeekContinuousDownTrend } from './monitor.util'

export const MonitorWeekDayPostCount = () => {
  const divId = 'weeklyPostCountChartDiv'

  const [year, setYear] = React.useState(moment())
  const [chart, setChart] = React.useState<Chart>()

  const ref = React.useRef<HTMLDivElement>(null)

  const { data } = useGetStatisticsQuery({
    variables: {
      input: {
        year: year.year(),
      },
      withWeeklyPostCount: true,
    },
  })

  // 折线图补全所有 value 为 0 的 week
  const weeklyPostsCountData = compensateWeekData(year, data?.statistics.weeklyPostsCount || [])

  React.useEffect(() => {
    import('../chart.component').then(({ default: Chart }) => {
      const newChart = new Chart({
        container: divId,
        autoFit: true,
        syncViewPadding: true,
      })
      newChart.animate(false)
      setChart(newChart)
    })
  }, [])

  React.useEffect(() => {
    if (weeklyPostsCountData && chart) {
      chart.clear()
      chart.scale({
        Week: {
          range: [0, 1],
          sync: true,
          nice: true,
        },
        PostCount: {
          alias: '发帖数',
          min: 0,
          max: 200,
        },
        YearMonth: {
          alias: '月份',
        },
      })

      const view = chart.createView()
      view.data(
        weeklyPostsCountData.map((row) => {
          const [year, week] = row.yearWeek!.split('-').map((s: string) => Number(s))
          return {
            ...row,
            Week: row.yearWeek,
            PostCount: row.value,
            YearMonth: moment().year(year).week(week).format('YYYY-MM'),
          }
        })
      )
      view.axis('Week', {
        tickLine: {
          alignTick: true,
          length: 6,
        },
      })
      view.axis('PostCount', true)
      view.line().position('Week*PostCount').tooltip('PostCount*YearMonth')

      view.point().position('Week*PostCount').label('PostCount')

      const downTrend = getAllThreeWeekContinuousDownTrend(weeklyPostsCountData)
      downTrend.map((data) => {
        view.annotation().dataRegion({
          start: data.start as any,
          end: data.end as any,
          text: {
            content: '',
          },
          lineLength: 50,
          region: {
            style: {
              fill: '#ff4d4f',
            },
          },
        })
      })

      chart.tooltip({
        title: (_, data) => data.Week + ' 周',
      })
      chart.render()
    }
  }, [chart, weeklyPostsCountData])

  const header = (title: string) => (
    <div style={{ display: 'flex', fontSize: 32, margin: '16px 0 8px 0', justifyContent: 'center' }}>
      <LineChartOutlined />
      <Typography.Title style={{ marginLeft: 8 }} level={3}>
        {title}
      </Typography.Title>
    </div>
  )

  return (
    <div className={s.chartContainer}>
      {header('周发帖数')}
      <div className={s.actions}>
        <DatePicker value={year} onChange={(value) => value && setYear(value)} picker='year' />
      </div>
      <div id={divId} className={s.chart} ref={ref} />
    </div>
  )
}
